import React, { useEffect, useRef } from "react";

import { useDrag, useDrop } from "react-dnd";

import { DRAG_TYPE } from "../../constants/dragTypes";

import type { JSX } from "react";

import type {
  DropzoneItem,
  DropzoneRenderItem,
  InitialDropzoneItem,
} from "./DropzoneListContainer";
import type { DropzoneMoveItemParams } from "../../types/fast";

export const DROPZONE_MEDIA_ITEM_HEIGHT = 80;

interface DropzoneListItemProps<T> {
  acceptList: string[];
  item: T & DropzoneItem;
  onDeleteClick: (uuid: string) => void;
  onMoveItem: ({ itemUuid, newRank, newItem }: DropzoneMoveItemParams) => void;
  isNewMediaDropDisabled?: boolean;
  isDragDisabled: boolean;
  renderItem: DropzoneRenderItem<T>;
  displayRank: number;
}

function DropzoneMediaItem<T extends InitialDropzoneItem>({
  item,
  onDeleteClick,
  onMoveItem,
  isDragDisabled,
  acceptList,
  isNewMediaDropDisabled,
  renderItem: RenderItem,
  displayRank,
}: DropzoneListItemProps<T>): JSX.Element {
  const itemContainerRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: DRAG_TYPE.DROPZONE_ITEM,
    item: () => item,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: [DRAG_TYPE.DROPZONE_ITEM, ...acceptList],
    hover(dragMedia: T & DropzoneItem, monitor) {
      const type = monitor.getItemType();

      if (type !== DRAG_TYPE.DROPZONE_ITEM && isNewMediaDropDisabled) return;

      const isDropzoneMedia = type === DRAG_TYPE.DROPZONE_ITEM;

      if (!itemContainerRef.current) {
        return;
      }

      const dragRank = dragMedia.rank;
      const hoverRank = item.rank;
      const isSameItem = dragMedia.uuid === item.uuid;
      const isSameContainer =
        dragMedia.dropzoneContainerId === item.dropzoneContainerId;

      if ((isDropzoneMedia && !isSameContainer) || isSameItem) {
        return;
      }

      const hoverBoundingRect =
        itemContainerRef.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      const isBeforeButTooHigh =
        dragRank < hoverRank && hoverClientY < hoverMiddleY;

      if (isBeforeButTooHigh) {
        return;
      }

      const isAfterButTooLow =
        dragRank > hoverRank && hoverClientY > hoverMiddleY;

      if (isAfterButTooLow) {
        return;
      }

      onMoveItem({
        itemUuid: dragMedia.uuid,
        newRank: hoverRank,
        newItem: !isDropzoneMedia ? dragMedia : undefined,
      });

      dragMedia.rank = hoverRank; // eslint-disable-line no-param-reassign
    },
  });

  useEffect(() => {
    previewRef(drop(itemContainerRef));
  }, [drop, previewRef]);

  return (
    <div
      ref={itemContainerRef}
      className="bg-card absolute inset-x-0 flex h-[4.375rem] items-stretch drop-shadow-sm will-change-transform"
      style={{
        opacity: isDragging || item.isTemp ? 0 : 1,
        transition: "transform 0.2s",
        transform: `translateY(${displayRank * DROPZONE_MEDIA_ITEM_HEIGHT}px)`,
      }}
    >
      <RenderItem
        dragRef={dragRef}
        isDragDisabled={isDragDisabled}
        isDragging={isDragging}
        item={item}
        onDeleteClick={onDeleteClick}
      />
    </div>
  );
}

export default DropzoneMediaItem;
